import React from "react";
import { cloneDeep } from "lodash";
import classnames from "classnames";
import PropTypes from "prop-types";
import { DataTable } from "@learningblocks/shared-components/lib/DataTable";
import LogEvent from "@learningblocks/shared-components/lib/EventLogger/LogEvent";
import { QuestionWrapper } from "../Components/QuestionWrapper";
import { MainTitle } from "../Components/MainTitle";
import { Question } from "../Components/Question";
import { Footer } from "../Components/Footer";
import { TotalAttempts } from "../Components/TotalAttempts";
import { Button } from "@labxchange/buttons";
import { HintArea } from "../Components/HintArea";
import { HintButton } from "../Components/HintButton";
import { useIntl } from "react-intl";
import { QuestionImage } from "../Components/QuestionImage";
import { ContentDropdown } from "../../ContentDropdown";
import ALERT_BALANCED from "@common/images/alert_balanced.svg";
import ALERT_UNBALANCED from "@common/images/alert_unbalanced.svg";
import { RichText } from "@learningblocks/shared-components/lib/RichText";

import "./style.scss";

/**
+ * Safari 13 fallback for structuredClone
+ */
function deepClone(obj) {
  return typeof structuredClone === "function"
    ? structuredClone(obj)
    : JSON.parse(JSON.stringify(obj));
}

export function QuestionTable(props) {
  const { hideAttemptsLeft = false } = props;
  const { showQuestionHeading = true } = props;
  const { cellHeight } = props;

  const intl = useIntl();
  const isCorrect = () => {
    let correct = 1;
    cloneDeep(props.data).forEach((row) => {
      row.forEach((item) => {
        if (item.type === "INPUT") {
          if (typeof item.answer === "string") {
            if (item.answer.toLowerCase() !== item.value.toLowerCase()) {
              correct = 0;
            }
          }
          if (typeof item.answer === "number") {
            if (item.answer !== item.value) {
              correct = 0;
            }
          }
          if (
            typeof item.answer === "object" &&
            item.answer !== null &&
            Object.hasOwn(item.answer, "min") &&
            item.value < item.answer.min
          ) {
            correct = 0;
          }
          if (
            typeof item.answer === "object" &&
            item.answer !== null &&
            Object.hasOwn(item.answer, "max") &&
            item.value > item.answer.max
          ) {
            correct = 0;
          }
          if (Array.isArray(item.answer)) {
            if (!item.answer.includes(item.value)) {
              correct = 0;
            }
          }
          const uniqueResponses =
            props.responses && Array.isArray(props.responses)
              ? props.responses
                  .filter((item) => item.unique === true)
                  .map((item) => item.value)
              : [];
          if (
            uniqueResponses.includes(item.value) &&
            uniqueResponses.filter((res) => res === item.value).length > 1
          ) {
            correct = 0;
          }
        }
      });
    });
    return correct;
  };

  const concatHints = () => {
    return props?.hints?.slice(0, props?.hintIndex + 1).map((hint) => {
      const hintParams = deepClone(hint);
      if (props.titleRows) hintParams.rowId -= props.titleRows;

      return (
        <p>{`row: ${hintParams.rowId}, col: ${hintParams.colId}, feedback: ${hintParams.feedback}`}</p>
      );
    });
  };

  const onSubmit = () => {
    // Reset hint state when submitting a new set of answers.
    if (props.showHint) {
      props.setShowHint({
        componentID: props.id,
        showHint: false,
      });
    }

    props.submitAnswer(props.id, props.type);
  };

  const onCellChange = (params) => {
    // Run the cell change action.
    props.onCellChange({
      componentID: props.id,
      ...params,
    });
  };

  /**
   * Check if text contains MathJax expressions
   * @param {string} text - Text to check
   * @returns {boolean} True if text contains MathJax expressions
   */
  const containsMathJax = (text) => {
    if (!text || typeof text !== "string") return false;
    // Check for MathJax patterns: $$...$$ or $...$
    return /\$\$.*?\$\$|\$.*?\$/g.test(text);
  };

  /**
   * Render text with mixed MathJax and plain text content
   * Handles legacy pattern conversion and modern MathJax rendering
   * @param {string} text - Text that may contain both MathJax and plain text
   * @returns {JSX.Element|string} Rendered content
   */
  const renderMixedContent = (text) => {
    if (!text || typeof text !== "string") return text;

    return <RichText value={text} equation={containsMathJax(text)} />;
  };

  const getFeedback = (isCorrect = false) => {
    const defaultFeedback = isCorrect ? "Correct!" : "Not Quite!";
    const feedbackKey = isCorrect ? "correctFeedback" : "incorrectFeedback";

    if (!props[feedbackKey]) return defaultFeedback;

    if (props[feedbackKey] === "[inputs]" && Array.isArray(props.responses)) {
      const uniqueFeedbacks = [
        ...new Set(
          props.responses
            .filter((r) => !r.disabled || !r?.correct)
            .map((r) => r?.feedback)
        ),
      ];
      if (uniqueFeedbacks.length === 0) return defaultFeedback;
      const intl = useIntl();
      const translatedFeedbacks = uniqueFeedbacks.map(
        (feedback) =>
          feedback &&
          intl.formatMessage({ id: feedback, defaultMessage: feedback })
      );
      return translatedFeedbacks.join(" ");
    }

    const intl = useIntl();
    const feedback = intl.formatMessage({
      id: props[feedbackKey],
      defaultMessage: props[feedbackKey],
    });
    const data =
      props.data[props[feedbackKey].rowId]?.[props[feedbackKey].colId] ||
      feedback;

    if (!data) return defaultFeedback;

    const finalFeedback = (
      data[feedbackKey] ||
      feedback ||
      defaultFeedback
    ).replace("[ANSWER]", data.answer);

    return finalFeedback;
  };

  const getImageUrl = (imageUrl) => {
    const baseUrl = `${window.location.origin}${window.location.pathname}locales/${intl.locale}/images/`;
    return imageUrl.startsWith(baseUrl) ? imageUrl : baseUrl + imageUrl;
  };
  if (props.image && props.image.src) {
    props.image.src = getImageUrl(props.image.src);
  }

  const buttonText = {
    id: props.buttonText || "submit_answer",
    defaultMessage: props.buttonText || "Submit answer",
    description: "",
  };

  return (
    <QuestionWrapper
      className={classnames({
        questionTable: true,
        noHeading: !showQuestionHeading,
      })}
    >
      {showQuestionHeading && (
        <MainTitle
          title={intl.formatMessage({
            id: props.heading ?? "Question",
            defaultMessage: props.heading ?? "Question",
            description: "",
          })}
          className="questionTable__mainTitle"
        />
      )}
      {props.image && (
        <QuestionImage
          tabIndex={0}
          altText={props.image.altText}
          image={props.image}
          className="cscq__image"
          preview={props.preview}
        />
      )}
      <Question text={props.question} className="questionTable__question" />
      <Question text={props.text} className="questionTable__question" />
      <DataTable
        className={"questionTable__tableWrapper"}
        indices={props.indices}
        data={props.data}
        onCellChange={onCellChange}
        uniqueResponses={
          props.responses && Array.isArray(props.responses)
            ? props.responses
                .filter((item) => item.unique === true)
                .map((item) => item.value)
            : []
        }
        cellHeight={cellHeight}
      />
      {props.showHint && <HintArea hint={concatHints()} />}
      {props.correct === true || props.correct === false ? (
        <div
          className={`correctStatus ${props.correct}`}
          style={{ marginTop: "16px" }}
        >
          {props.correct ? (
            <div
              style={{
                borderRadius: "5px",
                border: "1px solid #84BE60",
                background: "#F3F8EF",
                padding: "8px",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <img src={ALERT_BALANCED} alt="Correct answer" />
              <span style={{ marginLeft: "8px" }}>
                {renderMixedContent(getFeedback(true))}
              </span>
            </div>
          ) : (
            <div
              style={{
                borderRadius: "5px",
                border: "1px solid #ED3431",
                background: "#FDEBEA",
                padding: "8px",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <img src={ALERT_UNBALANCED} alt="Incorrect answer" />
              <span style={{ marginLeft: "8px" }}>
                {renderMixedContent(getFeedback(false))}
              </span>
            </div>
          )}
        </div>
      ) : null}
      {props.hintDetails && (
        <ContentDropdown contentDetails={props.hintDetails} />
      )}
      {!props.hideFooter && (
        <Footer className="questionTable__footer">
          <TotalAttempts
            hideAttemptsLeft={hideAttemptsLeft}
            totalAttempts={props.totalAttempts}
            className="questionTable__footer__totalAttempts"
          />
          {props?.hints?.length > props?.hintIndex + 1 ||
          (props?.hints?.length === props?.hintIndex + 1 && !props.showHint) ? (
            <HintButton
              disabled={false}
              firstHint={props?.showHint ? false : true}
              showHint={() => {
                if (!props.showHint) {
                  props.setShowHint({
                    componentID: props.id,
                    showHint: true,
                  });
                } else {
                  props.showNextHint({
                    componentID: props.id,
                  });
                }
              }}
            />
          ) : null}
          <LogEvent
            logEventProps={{
              event_type: "sim_numericalquestion submitted",
              event_schema: "checksubmission",
              question_number: props.id,
              correct: isCorrect(),
            }}
            actionProps={{ onSubmit: { action: "submit" } }}
            elementType="button"
          >
            <Button
              className="primary-button"
              onClick={onSubmit}
              disabled={props.submitDisabled}
              titleDescriptor={buttonText}
              iconName={props.iconName ? props.iconName : null}
            />
          </LogEvent>
        </Footer>
      )}
    </QuestionWrapper>
  );
}

QuestionTable.propTypes = {
  /**
   * Question for the component
   */
  question: PropTypes.string,
  /**
   * The prop that hold image data
   */
  image: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /**
   * Number of [rows, columns] in the table
   */
  indices: PropTypes.arrayOf(PropTypes.number),
  /**
   * Data to be rendered in the table
   */
  data: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        /**
         * The type of the cell
         */
        type: PropTypes.oneOf(["TITLE", "FIXED", "INPUT", "DROPDOWN"]),
        /**
         * Text to render in cell [ TITLE, FIXED]
         */
        text: PropTypes.text,
        /**
         * Type of the input [ INPUT ]
         */
        inputType: PropTypes.oneOf(["number", "text"]),
        /**
         * placeholder of for cell [ DROPDOWN, INPUT]
         */
        placeholder: PropTypes.string,
        /**
         * options to display on dropdown [DROPDOWN]
         */
        options: PropTypes.array,
        /**
         * Boolean to disable the cell
         */
        disabled: PropTypes.bool,
      })
    )
  ),
  /**
   * Function to invoke when the data in a cell changes
   */
  onCellChange: PropTypes.func,
  /**
   * Boolean to set if quesiton is complete
   */
  complete: PropTypes.bool,
  /**
   * UniqueId for the question component
   */
  id: PropTypes.number,
  /**
   * Total number of  attempts to solve question
   */
  totalAttempts: PropTypes.number,
  /**
   * Function to invoke on submit button click
   */
  submitAnswer: PropTypes.func,
  /**
   * Custom text for the submit button
   */
  buttonText: PropTypes.string,
  /**
   * Boolean to set submit button enabled/disabled
   */
  submitDisabled: PropTypes.bool,
  /**
   * Array of user responses for the table
   */
  responses: PropTypes.arrayOf(
    PropTypes.shape({
      rowId: PropTypes.number,
      colId: PropTypes.number,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      unique: PropTypes.bool,
    })
  ),
};
